// The map spot of a drill step on the engine (arcade.md §17.8, R1573,
// mockup round 075 card 13).
//
// The spot is the cell itself: its outline dashed in the neutral marker
// ink (arcade.md §4.9), and four arrowheads on the four screen axes that
// close on it once a period. The map already spends rings on sonar, on
// anchors, on control points and on the gun ring, so the spot draws no ring
// of its own, and the arrowheads stand square to the screen, where they
// never read as the corner brackets of a selection. When the spot is off
// the glass, one arrow stands inside the screen edge on its bearing.
//
// A step that names a hull class (`ArcadeDrillStep.select`) marks one own
// hull of that class while no hull or a hull of another class is selected
// (arcade.md §17.8): the same four arrowheads close on the hull and follow
// it, and the same edge arrow stands for it off the glass. The match picks
// the hull (`arcade/drill.ts` `markedHull`); the layer asks the fleet layer
// where it stands on every frame.
//
// The step-done moment of card 14 adds one flash where the act landed: a
// white disc that grows and fades. Reduced motion holds the arrowheads at
// the middle of their path and draws no flash.
//
// The card draws its figures at a cell of DRILL_SPOT_REF_CELL_PX, and this
// layer scales them by HEX_SIZE over that figure, so the spot keeps the
// card's proportions against the cell it marks. The edge arrow is a mark on
// the glass, so its figures are CSS px at every zoom.
//
// The cost (ui-map-interface.md §10): the outline is static and packed once
// a spot; the arrowheads, the edge arrows and the flash are one quad batch
// and one circle batch of at most ten and one instances, packed while a
// spot, a hull mark or a flash stands. Both shapes draw from the one baked dart cell
// (`fast/gfx/atlas.ts` `drillSpecFor`), so the layer adds no program to
// either backend.

import {
  DRILL_EDGE_ALPHA, DRILL_EDGE_INSET_PX, DRILL_EDGE_LEN_PX, DRILL_EDGE_W_PX, DRILL_FLASH_ALPHA,
  DRILL_FLASH_MS, DRILL_FLASH_R0_PX, DRILL_FLASH_R1_PX, DRILL_SPOT_ALPHA_FAR,
  DRILL_SPOT_ALPHA_NEAR, DRILL_SPOT_ALPHA_STILL, DRILL_SPOT_ARROW_FAR_PX,
  DRILL_SPOT_ARROW_LEN_PX, DRILL_SPOT_ARROW_NEAR_PX, DRILL_SPOT_ARROW_W_PX, DRILL_SPOT_DASH_PX,
  DRILL_SPOT_LINE_ALPHA, DRILL_SPOT_LINE_PX, DRILL_SPOT_PERIOD_SEC, DRILL_SPOT_REF_CELL_PX,
  MARKER_NEUTRAL,
} from '../../gen/presentation';
import { DART_CELL_LEN, DART_CELL_W, DRILL_DART_KEY } from '../../render/drill-dart';
import { reducedMotion } from '../../render/motion';
import type { ViewRect } from '../../render/cull';
import { HEX_SIZE, cellCenter } from '../../world';
import type { DrawCtx, FrameInfo } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink, SpriteEntry } from './batches';
import { hexOutline, zoomOf } from './shared';

/** Four arrowheads and one edge arrow for the spot, and as many for the
 *  marked hull. */
const MAX_DARTS = 10;

/** The scale from the card's reference cell to the map's own cell. */
export function spotScale(): number {
  return HEX_SIZE / DRILL_SPOT_REF_CELL_PX;
}

/**
 * Where the arrowheads stand at a clock, in world px: the distance of each
 * tip from the centre, and the alpha. A period closes them from the far
 * radius to the near one and fades them; reduced motion holds them at the
 * middle of the path, at the still alpha.
 */
export function arrowAt(timeS: number, still: boolean,
  out: { r: number; alpha: number } = { r: 0, alpha: 0 }): { r: number; alpha: number } {
  const k = spotScale();
  const far = DRILL_SPOT_ARROW_FAR_PX, near = DRILL_SPOT_ARROW_NEAR_PX;
  if (still) {
    out.r = k * (far + (near - far) * 0.5);
    out.alpha = DRILL_SPOT_ALPHA_STILL;
    return out;
  }
  const ph = (((timeS % DRILL_SPOT_PERIOD_SEC) + DRILL_SPOT_PERIOD_SEC) % DRILL_SPOT_PERIOD_SEC)
    / DRILL_SPOT_PERIOD_SEC;
  out.r = k * (far + (near - far) * ph);
  out.alpha = DRILL_SPOT_ALPHA_FAR + (DRILL_SPOT_ALPHA_NEAR - DRILL_SPOT_ALPHA_FAR) * ph;
  return out;
}

/** One placed dart: its centre in world px and its rotation, pointing
 *  along +x at 0. */
export interface Dart { x: number; y: number; rot: number }

/** Four darts to write into, so a frame allocates none (§10). */
function newDarts(n: number): Dart[] {
  return Array.from({ length: n }, () => ({ x: 0, y: 0, rot: 0 }));
}

/** The four screen axes: east, south, west, north. */
const AXES: readonly (readonly [number, number])[] = [[1, 0], [0, 1], [-1, 0], [0, -1]];

/**
 * The four arrowheads around a spot at tip distance `r`, written into
 * `out`. Each one stands on a screen axis outside the cell and points at
 * the centre.
 */
export function arrowheads(x: number, y: number, r: number, out: Dart[] = newDarts(4)): Dart[] {
  const half = (spotScale() * DRILL_SPOT_ARROW_LEN_PX) / 2;
  for (let k = 0; k < 4; k++) {
    const [ux, uy] = AXES[k];
    const d = out[k];
    d.x = x + ux * (r + half);
    d.y = y + uy * (r + half);
    d.rot = (k * Math.PI) / 2 + Math.PI;
  }
  return out;
}

/**
 * The edge arrow of a spot off the glass, or null while the spot's centre
 * is inside the view. It stands on the line from the middle of the view to
 * the spot, `DRILL_EDGE_INSET_PX` CSS px inside the nearer edge, and
 * points at the spot.
 */
export function edgeArrow(view: ViewRect, zoom: number, x: number, y: number,
  out: Dart = { x: 0, y: 0, rot: 0 }): Dart | null {
  if (x > view.x0 && x < view.x1 && y > view.y0 && y < view.y1) return null;
  const mx = (view.x0 + view.x1) / 2, my = (view.y0 + view.y1) / 2;
  const inset = DRILL_EDGE_INSET_PX / zoomOf(zoom);
  const hw = Math.max(0, (view.x1 - view.x0) / 2 - inset);
  const hh = Math.max(0, (view.y1 - view.y0) / 2 - inset);
  const dx = x - mx, dy = y - my;
  const k = Math.min(hw / Math.max(Math.abs(dx), 1e-6), hh / Math.max(Math.abs(dy), 1e-6));
  out.x = mx + dx * k;
  out.y = my + dy * k;
  out.rot = Math.atan2(dy, dx);
  return out;
}

/** The flash at an age in ms: its radius in world px and its alpha, written
 *  into `out`, or null once it has run. */
export function flashAt(ageMs: number,
  out: { r: number; alpha: number } = { r: 0, alpha: 0 }): { r: number; alpha: number } | null {
  if (ageMs < 0 || ageMs >= DRILL_FLASH_MS) return null;
  const t = ageMs / DRILL_FLASH_MS;
  out.r = spotScale() * (DRILL_FLASH_R0_PX + (DRILL_FLASH_R1_PX - DRILL_FLASH_R0_PX) * t);
  out.alpha = DRILL_FLASH_ALPHA * (1 - t);
  return out;
}

/** The spot as the layer takes it: the cell it marks. */
export interface DrillSpotCell { cell: number }

/** Where one hull stands now in world px, or null while the fleet layer
 *  does not hold it. The answer may be a reused object: the layer reads it
 *  at once. */
export type HullAt = (id: number) => { x: number; y: number } | null;

/**
 * The marks of one drill step on the water. `MarkerLayer` owns one and
 * builds it on the first spot, so a page that plays no drill asks the
 * engine for no batch.
 */
export class DrillSpot {
  private readonly outline: SegmentSink;
  private readonly darts: QuadSink;
  private readonly flashes: CircleSink;
  private readonly dart: SpriteEntry;
  private cell = -1;
  private x = 0;
  private y = 0;
  /** The own hull the step marks, or 0. */
  private hull = 0;
  private flashX = 0;
  private flashY = 0;
  /** When the flash started, on the frame clock in ms, or -1. */
  private flashFrom = -1;
  /** A flash asked for between two frames, which the next frame starts. */
  private flashDue = false;
  /** The darts a frame writes, kept so a frame allocates none. */
  private readonly heads = newDarts(4);
  private readonly edge: Dart = { x: 0, y: 0, rot: 0 };
  private readonly at = { r: 0, alpha: 0 };
  private readonly flashNow = { r: 0, alpha: 0 };

  constructor(src: BatchSource, private readonly still: () => boolean = reducedMotion,
    private readonly hullAt: HullAt = () => null) {
    this.outline = src.newSegments(6);
    this.darts = src.newQuads(MAX_DARTS);
    this.flashes = src.newCircles(1);
    this.dart = src.sprite(DRILL_DART_KEY);
  }

  /** The cell the spot marks, or -1. The tests read it. */
  get spotCell(): number { return this.cell; }

  /** The hull the layer marks, or 0. The tests read it. */
  get hullId(): number { return this.hull; }

  /** Takes the own hull the step marks, or 0 for none. */
  setHull(id: number): void { this.hull = id; }

  /** Takes the spot of the step that stands, or null for none. The same
   *  cell twice packs nothing. */
  set(spot: DrillSpotCell | null): void {
    const cell = spot?.cell ?? -1;
    if (cell === this.cell) return;
    this.cell = cell;
    const o = this.outline;
    o.begin();
    if (cell < 0) { this.darts.begin(); return; }
    const [x, y] = cellCenter(cell);
    this.x = x;
    this.y = y;
    const k = spotScale();
    const [on, off] = DRILL_SPOT_DASH_PX;
    hexOutline(o, x, y, k * DRILL_SPOT_LINE_PX, MARKER_NEUTRAL, DRILL_SPOT_LINE_ALPHA,
      0, k * on, k * off);
  }

  /** One flash where an act landed, in world px. Reduced motion draws
   *  none (card 14's still variant). */
  flash(x: number, y: number): void {
    if (this.still()) return;
    this.flashX = x;
    this.flashY = y;
    this.flashDue = true;
  }

  /** True while the layer has a mark to draw. */
  get active(): boolean {
    return this.cell >= 0 || this.hull !== 0 || this.flashDue || this.flashFrom >= 0;
  }

  update(frame: FrameInfo): void {
    const d = this.darts;
    d.begin();
    if (this.cell >= 0) this.packMark(frame, this.x, this.y);
    const hull = this.hull !== 0 ? this.hullAt(this.hull) : null;
    if (hull !== null) this.packMark(frame, hull.x, hull.y);
    const f = this.flashes;
    f.begin();
    if (this.flashDue) { this.flashDue = false; this.flashFrom = frame.now; }
    if (this.flashFrom >= 0) {
      const now = flashAt(frame.now - this.flashFrom, this.flashNow);
      if (now === null) this.flashFrom = -1;
      else f.disc(this.flashX, this.flashY, now.r, MARKER_NEUTRAL, now.alpha);
    }
  }

  draw(ctx: DrawCtx): void {
    this.outline.flush(ctx.cam);
    this.darts.flush(ctx.cam);
    this.flashes.flush(ctx.cam);
  }

  /** The four arrowheads round one point, and the edge arrow while the
   *  point is off the glass. */
  private packMark(frame: FrameInfo, x: number, y: number): void {
    const at = arrowAt(frame.timeS, this.still(), this.at);
    const k = spotScale();
    const len = k * DRILL_SPOT_ARROW_LEN_PX, w = k * DRILL_SPOT_ARROW_W_PX;
    for (const a of arrowheads(x, y, at.r, this.heads)) this.packDart(a, len, w, at.alpha);
    const edge = edgeArrow(frame.view, frame.cam.zoom, x, y, this.edge);
    if (edge !== null) {
      const z = 1 / zoomOf(frame.cam.zoom);
      this.packDart(edge, DRILL_EDGE_LEN_PX * z, DRILL_EDGE_W_PX * z, DRILL_EDGE_ALPHA);
    }
  }

  /** One dart from the baked cell, stretched to `len` by `w` world px. The
   *  cell's box carries a pad, so the stretch scales the whole box. */
  private packDart(a: Dart, len: number, w: number, alpha: number): void {
    const e = this.dart;
    const sx = len / DART_CELL_LEN, sy = w / DART_CELL_W;
    this.darts.quad(a.x, a.y, 0, 0, e.w * sx, e.h * sy, a.rot, 0, MARKER_NEUTRAL, alpha,
      e.u0, e.v0, e.u1, e.v1);
  }
}
